import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExternalLink, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import EvidenceBadge from '@/components/EvidenceBadge';
import ContraindicationBox from '@/components/ContraindicationBox';
import { PROTO } from '@/constants/testIds/healthspan';

const mechanismLabel = (m) => m.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function ProtocolCard({ protocol, score, applicable, applicabilityNotes = [], contraindicationNotes = [], scoreBreakdown }) {
  const nav = useNavigate();
  const p = protocol;
  return (
    <Card data-testid={PROTO.card} className="rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)] shadow-[var(--shadow-sm)] hover:border-[color:var(--border-strong)] transition-colors duration-150 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-semibold tracking-tight">{p.title}</h3>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-[color:var(--surface-2)] border border-[color:var(--border-color)] px-2.5 py-0.5 text-[11px] text-[color:var(--muted-fg)]">{mechanismLabel(p.mechanism)}</span>
            <span className="inline-flex items-center rounded-full bg-[color:var(--surface-2)] border border-[color:var(--border-color)] px-2.5 py-0.5 text-[11px] text-[color:var(--muted-fg)]">{mechanismLabel(p.intervention_type)}</span>
            {p.is_placeholder_citation && (
              <span data-testid={PROTO.citationsPending} className="inline-flex items-center rounded-full border border-[color:var(--border-color)] px-2.5 py-0.5 text-[11px] text-[color:var(--muted-fg)]">Citations pending</span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <EvidenceBadge level={p.evidence_level} />
          {typeof score === 'number' && (
            <span title="Composite ranking score" className="tabular-nums text-[11px] text-[color:var(--muted-fg)]">Match {(score * 100).toFixed(0)}%</span>
          )}
        </div>
      </div>

      <p className="mt-3 text-sm leading-6 text-[color:var(--muted-fg)]">{p.summary_public}</p>

      {applicable === false && (
        <div className="mt-3 text-xs rounded-[var(--radius-sm)] border border-[color:rgba(216,155,0,0.45)] bg-[color:rgba(216,155,0,0.08)] px-3 py-2 text-[color:var(--warning)]">
          Not typically applicable to this profile yet — see notes.
        </div>
      )}
      {applicabilityNotes && applicabilityNotes.length > 0 && (
        <ul className="mt-3 text-xs text-[color:var(--muted-fg)] list-disc pl-5 space-y-0.5">
          {applicabilityNotes.map((n, i) => (<li key={i}>{n}</li>))}
        </ul>
      )}

      <ContraindicationBox items={contraindicationNotes.length ? contraindicationNotes : p.contraindications || []} />

      {p.tier_gated && (
        <div className="mt-4 flex items-center gap-2 text-xs text-[color:var(--muted-fg)]">
          <Lock className="h-3.5 w-3.5" aria-hidden="true" /> Researcher-only technical summary available with upgrade.
        </div>
      )}

      <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-[color:var(--border-color)]">
        <a
          data-testid={PROTO.sourceLink}
          href={p.source_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-[color:var(--accent-teal)] underline underline-offset-4 hover:no-underline transition-colors duration-150"
        >
          View primary source <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
        <Button data-testid={PROTO.viewButton} variant="outline" size="sm" onClick={() => nav(`/protocols/${p.id}`)}>View details</Button>
      </div>
    </Card>
  );
}
